import React from 'react';
import { Dialog, DialogContent, DialogActions, Stack } from '@mui/material';
import styled from 'styled-components';
import { Button } from './buttons';
import { ErrorDialogAction } from './error-dialog';

export type { ErrorDialogAction };

export type FeedbackDialogVariant = 'error' | 'success' | 'warning' | 'info' | 'custom';

export type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
  variant: FeedbackDialogVariant;
  title: string;
  message: string;
  subtitle?: string;
  icon?: React.ReactNode;
  ctas?: ErrorDialogAction[];
  leftActionSlot?: React.ReactNode;
  // Only applied when variant === 'custom'
  accent?: string;
  iconColor?: string;
};

const VARIANT_CONFIG: Record<
  Exclude<FeedbackDialogVariant, 'custom'>,
  { accent: string; iconColor: string; renderIcon: (accent: string, iconColor: string) => React.ReactNode }
> = {
  error: {
    accent: '#d32f2f',
    iconColor: '#fff',
    renderIcon: (accent, iconColor) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" fill={accent} />
        <path d="M22 22 L42 42 M42 22 L22 42" stroke={iconColor} strokeWidth="4" strokeLinecap="round" />
      </svg>
    ),
  },
  success: {
    accent: '#2e7d32',
    iconColor: '#fff',
    renderIcon: (accent, iconColor) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" fill={accent} />
        <path
          d="M18 32 L28 43 L46 22"
          stroke={iconColor}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    ),
  },
  warning: {
    accent: '#ed6c02',
    iconColor: '#fff',
    renderIcon: (accent, iconColor) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <polygon points="32,6 60,56 4,56" fill={accent} />
        <line x1="32" y1="24" x2="32" y2="40" stroke={iconColor} strokeWidth="4" strokeLinecap="round" />
        <circle cx="32" cy="48" r="2.5" fill={iconColor} />
      </svg>
    ),
  },
  info: {
    accent: '#1F9ECC',
    iconColor: '#fff',
    renderIcon: (accent, iconColor) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="30" fill={accent} />
        <circle cx="32" cy="20" r="3" fill={iconColor} />
        <line x1="32" y1="28" x2="32" y2="46" stroke={iconColor} strokeWidth="4" strokeLinecap="round" />
      </svg>
    ),
  },
};

function resolveVariant(props: FeedbackDialogProps): { accent: string; icon: React.ReactNode } {
  if (props.icon) {
    const accent = props.variant === 'custom' ? (props.accent ?? '#1F9ECC') : VARIANT_CONFIG[props.variant].accent;
    return { accent, icon: props.icon };
  }

  if (props.variant === 'custom') {
    const accent = props.accent ?? '#1F9ECC';
    const iconColor = props.iconColor ?? '#fff';
    return {
      accent,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="30" fill={accent} />
          <circle cx="32" cy="20" r="3" fill={iconColor} />
          <line x1="32" y1="28" x2="32" y2="46" stroke={iconColor} strokeWidth="4" strokeLinecap="round" />
        </svg>
      ),
    };
  }

  const cfg = VARIANT_CONFIG[props.variant];
  return { accent: cfg.accent, icon: cfg.renderIcon(cfg.accent, cfg.iconColor) };
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = (props) => {
  const { open, onClose, title, message, subtitle, ctas, leftActionSlot } = props;
  const { accent, icon } = resolveVariant(props);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '4px', width: '90%', maxWidth: 640 } }}>
      <TopAccent $accent={accent} />
      <CloseButton onClick={onClose} aria-label="Fechar" title="Fechar">
        ×
      </CloseButton>
      <DialogContent sx={{ textAlign: 'center', paddingTop: '28px' }}>
        <IconWrapper>{icon}</IconWrapper>
        <Title>{title}</Title>
        {subtitle ? <SubTitle>{subtitle}</SubTitle> : null}
        <Message>{message}</Message>

        <ActionsWrapper direction="row" justifyContent="space-between" alignItems="center">
          {leftActionSlot ?? <span />}
          <Stack direction="row" gap="8px">
            {(ctas ?? []).map((action, idx) => (
              <Button
                key={idx}
                label={action.loading ? (action.loadingLabel ?? action.label) : action.label}
                view={action.view ?? 'primary'}
                disabled={Boolean(action.disabled || action.loading)}
                onClick={action.onClick}
                style={{ padding: '10px 18px' }}
              />
            ))}
          </Stack>
        </ActionsWrapper>
      </DialogContent>
      <DialogActions />
    </Dialog>
  );
};

const TopAccent = styled.div<{ $accent: string }>`
  height: 8px;
  width: 100%;
  background: ${(p) => p.$accent};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -12px;
  padding: 16px;
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 700;
  margin: 14px 0 4px;
  color: #000;
`;

const SubTitle = styled.p`
  margin: 0 0 10px;
  color: #707273;
  font-size: 14px;
`;

const Message = styled.p`
  color: #4b4f52;
  margin: 12px 0 22px;
`;

const ActionsWrapper = styled(Stack)`
  margin-top: 8px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 32px;
  line-height: 1;
  padding: 4px;
  cursor: pointer;
  &:hover {
    color: #374151;
  }
`;
